import React, { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { errorMessage } from "../../lib/api";
import { EMAIL_RE, submitForm } from "../../lib/forms";
import { cn } from "../../lib/utils";

const inputCls = "h-12 rounded-none border-border bg-transparent px-4 text-base placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";
const areaCls = "min-h-[130px] rounded-none border-border bg-transparent px-4 py-3 text-base placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const Field = ({ id, label, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </Label>
    {children}
    {error && (
      <p className="text-xs text-primary" data-testid={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

const initial = { name: "", email: "", phone: "", message: "", website: "" };

/**
 * Name / email / phone / message form shared by the site contact section and landing-page lead blocks.
 * Posts to the backend inbox; `extra` carries page_slug / block_id for landing pages.
 */
const LeadForm = ({ formType, showPhone = true, showMessage = true, requireMessage = false, submitLabel = "Send", successTitle = "Thanks!", successBody = "We'll be in touch shortly.", messagePlaceholder = "Tell us what you have in mind.", extra = {}, onSuccess, testId = "lead-form" }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name is required.";
    if (!EMAIL_RE.test(form.email)) e.email = "Enter a valid email address.";
    if (showMessage && requireMessage && form.message.trim().length < 10) e.message = "Tell us a little more (10+ characters).";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please fix the highlighted fields.");
    setBusy(true);
    try {
      const fields = { name: form.name.trim(), email: form.email.trim() };
      if (showPhone && form.phone.trim()) fields.phone = form.phone.trim();
      if (showMessage && form.message.trim()) fields.message = form.message.trim();
      await submitForm(formType, fields, { ...extra, website: form.website });
      setDone(fields.email);
      toast.success(successTitle);
      onSuccess?.(fields);
    } catch (err) {
      toast.error(errorMessage(err, "Could not send. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-start gap-5 py-4" data-testid={`${testId}-success`}>
        <span className="flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground">
          <CheckCircle2 className="h-6 w-6" />
        </span>
        <h3 className="display text-4xl uppercase leading-none tracking-wide md:text-5xl">{successTitle}</h3>
        <p className="max-w-md text-base leading-relaxed text-muted-foreground">
          {successBody} We&apos;ll reply to <span className="font-semibold text-foreground">{done}</span>.
        </p>
        <Button
          variant="outline"
          className="h-11 rounded-none border-foreground/30 bg-transparent px-6 text-[11px] font-semibold uppercase tracking-[0.2em] hover:bg-foreground hover:text-background"
          onClick={() => {
            setDone(null);
            setForm(initial);
          }}
          data-testid={`${testId}-again`}
        >
          Send another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid={testId}>
      <Field id={`${testId}-name`} label="Name *" error={errors.name}>
        <Input id={`${testId}-name`} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" autoComplete="name" className={cn(inputCls, errors.name && "border-primary")} data-testid={`${testId}-name`} />
      </Field>
      <Field id={`${testId}-email`} label="Email *" error={errors.email}>
        <Input id={`${testId}-email`} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" autoComplete="email" className={cn(inputCls, errors.email && "border-primary")} data-testid={`${testId}-email`} />
      </Field>
      {showPhone && (
        <div className="sm:col-span-2">
          <Field id={`${testId}-phone`} label="Phone (optional)">
            <Input id={`${testId}-phone`} type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="604-555-0100" autoComplete="tel" className={inputCls} data-testid={`${testId}-phone`} />
          </Field>
        </div>
      )}
      {showMessage && (
        <div className="sm:col-span-2">
          <Field id={`${testId}-message`} label={requireMessage ? "Message *" : "Message (optional)"} error={errors.message}>
            <Textarea id={`${testId}-message`} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={messagePlaceholder} rows={5} className={cn(areaCls, errors.message && "border-primary")} data-testid={`${testId}-message`} />
          </Field>
        </div>
      )}
      {/* Honeypot: hidden from people, filled by bots */}
      <input type="text" name="website" value={form.website} onChange={(e) => update("website", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <div className="sm:col-span-2">
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-none text-[11px] font-semibold uppercase tracking-[0.2em] sm:w-auto sm:px-10" data-testid={`${testId}-submit`}>
          {busy ? "Sending…" : submitLabel}
          {!busy && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </form>
  );
};

export default LeadForm;
